// System-status seam for degraded mode. Drives the app-wide
// DegradedBanner: when a dependency is slow/erroring or the client is offline,
// the shell says so plainly — what happened, that data is safe, and a retry —
// instead of pretending everything is fine (honest-interfaces principle).
//
// Wired to the real readiness probe: getSystemStatus() polls
// `GET /health/ready` (AppLayout sets the refetchInterval) and maps the outcome:
//   200            -> 'operational'  (everything up, no banner)
//   503 / 5xx body -> 'degraded'     (server reachable but a dep is failing)
//   no response    -> 'offline'      (network/DNS/CORS down; serve cached data)
// It never throws: a rejected probe would leave react-query with `undefined`
// data, which the shell reads as 'operational' — the opposite of the truth. So
// the offline case is returned as data, not surfaced as a query error.

import { isAxiosError } from 'axios'

import { apiClient } from '../../api/client.js'

export type SystemStatus = 'operational' | 'degraded' | 'offline'

export interface SystemStatusInfo {
  status: SystemStatus
  message: string
}

const MESSAGES: Record<Exclude<SystemStatus, 'operational'>, string> = {
  degraded:
    'Estamos teniendo problemas de conexión. INHERTA se está reconectando solo. Tu información está a salvo y no se perdió nada; algunas secciones pueden tardar en cargar.',
  offline:
    'Estás sin conexión. Te mostramos la información guardada; los cambios se sincronizan en cuanto vuelvas a conectarte.',
}

/** DegradedBanner copy for a status; '' for 'operational' (no banner). */
export function messageFor(status: SystemStatus): string {
  return status === 'operational' ? '' : MESSAGES[status]
}

/** A `systemStatus` query param (degraded|offline) forces the banner — a QA/demo
 *  hook so the state is reviewable without taking a real dependency down. */
export function statusOverrideFrom(search: string): SystemStatus | null {
  const v = new URLSearchParams(search).get('systemStatus')
  return v === 'degraded' || v === 'offline' ? v : null
}

/**
 * Probes GET /health/ready: 200 → 'operational', an answered error → 'degraded',
 * no response → 'offline'. Never throws — a rejected query would read as
 * 'operational' in the shell, the opposite of the truth.
 */
export async function getSystemStatus(): Promise<SystemStatusInfo> {
  try {
    // /health/ready 200 = DB + Redis reachable. A 503 (degraded body) throws.
    await apiClient.get('/health/ready')
    return { status: 'operational', message: '' }
  } catch (err) {
    // Server answered (e.g. 503 from /health/ready) -> reachable but degraded.
    // No `response` at all (network down, DNS, CORS) -> we're offline.
    const status: SystemStatus = isAxiosError(err) && err.response ? 'degraded' : 'offline'
    return { status, message: messageFor(status) }
  }
}
